import * as React from 'react';
import {
	Button,
	ButtonProps,
	FlyoverBuilder,
	IconProps,
	layout,
	palette,
	styleUtils,
} from '@citrite/web-ui-component';
import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { useFormFactor } from 'App/Header/useFormFactor';

interface FlyoverWrapperProps {
	/** The content to display in the flyover popup */
	flyoverContent: React.ReactNode;
	/** The icon component to display in the button */
	icon: React.FC<IconProps>;
	/** The button title text */
	title: string;
	/** The aria-label for accessibility */
	ariaLabel: string;
	/** The data-testid for testing */
	dataTestId: string;
	/** The HTML id attribute */
	id: string;
	/** Optional icon size */
	iconSize?: number;
	/** Whether to close flyover when clicking inside it */
	closeOnFlyoverClick?: boolean;
	/** Custom styles for the flyover popup */
	flyoverStyle?: React.CSSProperties;
	/** Custom styles for the button */
	buttonStyle?: React.CSSProperties;
	/** Whether to show only on large form factors (default: true) */
	showOnlyOnLargeFormFactor?: boolean;
	/** Optional notification icon to display on top of the main icon */
	notificationIcon?: React.ReactNode;
}

const defaultPopOverStyle: React.CSSProperties = {
	display: 'flex',
	backgroundColor: palette.white,
	border: '0.5px solid',
	borderColor: palette.grey500,
	boxShadow: styleUtils.shadow2.boxShadow,
	borderRadius: layout.tinySpace,
	scrollbarWidth: 'thin',
};

const StyledIconContainer = styled.div`
	position: relative;
`;

const defaultButtonStyle = {
	lineHeight: 0,
	display: 'inline-block',
	paddingRight: layout.smallSpace,
};

export const FlyoverWrapper: React.FC<FlyoverWrapperProps> = ({
	flyoverContent,
	icon,
	title,
	ariaLabel,
	dataTestId,
	id,
	iconSize = 25,
	closeOnFlyoverClick = false,
	flyoverStyle = defaultPopOverStyle,
	buttonStyle = defaultButtonStyle,
	showOnlyOnLargeFormFactor = true,
	notificationIcon,
}) => {
	const { isSmallFormFactor } = useFormFactor();
	const theme = useTheme();

	// Show component based on form factor preference
	const shouldShow = showOnlyOnLargeFormFactor ? !isSmallFormFactor : true;

	return (
		shouldShow && (
			<FlyoverBuilder
				closeOnFlyoverClick={closeOnFlyoverClick}
				toggleOnClick
				style={flyoverStyle}
				flyover={flyoverContent}
			>
				{(props: ButtonProps) => (
					<StyledIconContainer>
						<Button.IconOnly
							{...props}
							style={buttonStyle}
							icon={icon}
							size={iconSize}
							title={title}
							color={theme?.branding?.styles?.HeaderTextColor}
							aria-label={ariaLabel}
							data-testid={dataTestId}
							id={id}
						/>
						{notificationIcon}
					</StyledIconContainer>
				)}
			</FlyoverBuilder>
		)
	);
};
